/**
 * roomSender — l'émetteur du canal de données de la salle.
 *
 * Le protocole est celui d'aujourd'hui (`dataChannel.ts`) : mêmes noms d'événements, même format,
 * même résolution des destinataires (les identités dont l'id d'utilisateur est visé ; aucune
 * trouvée : un essai de plus 500 ms après, puis l'abandon), mêmes nouveaux essais sur une erreur
 * passagère du SDK (500, 1 000, 2 000 ms).
 *
 * Ce qui change, la règle du moteur : **on n'envoie qu'à une salle connectée.** Ailleurs, le message
 * est GARDÉ (`keep`, il part à la reconnexion : un tuteur qui doit apprendre qu'une copie est rendue)
 * ou ABANDONNÉ (`drop`, ce qu'un envoi tardif rendrait faux ou inutile : la frappe, un message que le
 * serveur a déjà), et l'émetteur le dit dans la télémétrie. L'ancien émetteur tentait à l'aveugle et
 * laissait le canal échouer sur une salle fermée (« salle fermée », l'erreur numéro 1 du projet).
 */
import { type EngineTelemetry } from "../../shared/telemetry"
import { encodeLiveEvent, userIdOfIdentity } from "./dataChannel"
import { toLiveTodoPayload } from "./liveTodoPayload"

/** Ce que l'émetteur fait d'un message quand la salle n'est pas connectée. */
export type SendPolicy = "keep" | "drop"

export type SendResult =
    { ok: true } | { ok: false; reason: "kept" | "dropped" | "destination_unreachable" | "publish_failed" }

export type RoomSenderDeps = {
    isConnected: () => boolean
    /** Les identités des autres participants, lues au moment de l'envoi. */
    remoteIdentities: () => string[]
    /** Un message, fiable, aux identités données (`null` : toute la salle). */
    publish: (bytes: Uint8Array<ArrayBuffer>, identities: string[] | null) => Promise<void>
    telemetry: EngineTelemetry
    sleep?: (ms: number) => Promise<void>
}

export type RoomSender = {
    send: (type: string, data: Record<string, unknown>, to: number[] | null, policy: SendPolicy) => Promise<SendResult>
    /** La salle est (re)connectée : les messages gardés partent, dans l'ordre. */
    flush: () => Promise<void>
    /** Combien de messages attendent la connexion. */
    pending: () => number
    /** On quitte la salle : ce qui attend est abandonné, et la télémétrie le dit. */
    clear: () => void
}

/** Les nouveaux essais d'une publication refusée par une erreur passagère (`useLiveKitEventSender`). */
export const RETRY_DELAYS_MS = [500, 1000, 2000]
/** Le second et dernier essai quand aucun destinataire n'est encore dans la salle. */
export const DESTINATION_RETRY_MS = 500
/** Au-delà, le plus ancien message gardé est abandonné : une coupure ne doit pas tout empiler. */
export const MAX_KEPT = 20

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export function isTransientPublishError(error: unknown): boolean {
    const message = (error instanceof Error ? error.message : String(error ?? "")).toLowerCase()
    return (
        message.includes("engine closed") ||
        message.includes("pc manager is closed") ||
        message.includes("pc manager closed") ||
        message.includes("message too large")
    )
}

type Kept = { type: string; data: Record<string, unknown>; to: number[] | null }

export function createRoomSender(deps: RoomSenderDeps): RoomSender {
    const sleep = deps.sleep ?? defaultSleep
    const { telemetry } = deps
    let kept: Kept[] = []

    const resolve = (to: number[] | null): string[] | null => {
        if (to == null || to.length === 0) return null
        const wanted = new Set(to.map(String))
        return deps.remoteIdentities().filter((identity) => wanted.has(userIdOfIdentity(identity) ?? ""))
    }

    const publishWithRetry = async (type: string, bytes: Uint8Array<ArrayBuffer>, identities: string[] | null) => {
        let lastError: unknown = null
        for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
            if (!deps.isConnected()) return false
            try {
                await deps.publish(bytes, identities)
                return true
            } catch (error) {
                lastError = error
                if (!isTransientPublishError(error)) break
                const delay = RETRY_DELAYS_MS[attempt]
                if (delay === undefined) break
                await sleep(delay)
            }
        }
        telemetry.error("send", lastError ?? new Error("publish failed"), { event: type, bytes: bytes.length })
        return false
    }

    const publish = async (item: Kept): Promise<SendResult> => {
        let identities = resolve(item.to)
        if (identities != null && identities.length === 0) {
            await sleep(DESTINATION_RETRY_MS)
            identities = resolve(item.to)
            if (identities != null && identities.length === 0) {
                // Le destinataire n'est pas dans la salle : rien ne lui parviendrait.
                telemetry.transition("sending", "abandoned", "destination_unreachable", { event: item.type })
                return { ok: false, reason: "destination_unreachable" }
            }
        }
        for (const bytes of encodeLiveEvent(item.type, item.data)) {
            const ok = await publishWithRetry(item.type, bytes, identities)
            if (!ok) return { ok: false, reason: "publish_failed" }
        }
        return { ok: true }
    }

    return {
        async send(type, data, to, policy) {
            // Une activité poussée part sans marques, l'énoncé marqué à côté : l'app de l'autre bout
            // peut être un ancien bundle (`liveTodoPayload`), comme avec l'émetteur d'avant.
            const item = {
                type,
                data: type === "newTodo" ? (toLiveTodoPayload(data) as Record<string, unknown>) : data,
                to,
            }
            if (!deps.isConnected()) {
                if (policy === "drop") {
                    telemetry.transition("offline", "dropped", "send", { event: type })
                    return { ok: false, reason: "dropped" }
                }
                kept = [...kept, item]
                if (kept.length > MAX_KEPT) {
                    const [oldest] = kept
                    kept = kept.slice(1)
                    telemetry.transition("kept", "dropped", "overflow", { event: oldest?.type ?? null })
                }
                telemetry.transition("offline", "kept", "send", { event: type, pending: kept.length })
                return { ok: false, reason: "kept" }
            }
            return publish(item)
        },
        async flush() {
            while (kept.length > 0 && deps.isConnected()) {
                const [next] = kept
                kept = kept.slice(1)
                if (next == null) break
                const result = await publish(next)
                telemetry.transition("kept", result.ok ? "sent" : "abandoned", "flush", { event: next.type })
            }
        },
        pending: () => kept.length,
        clear() {
            if (kept.length > 0) telemetry.transition("kept", "abandoned", "leave", { pending: kept.length })
            kept = []
        },
    }
}
